import type { RgbSource } from '../../types/api/macroCalibration';
import { VisualMaterialityPolicy } from './VisualMaterialityPolicy';

export type VisualCaseLabel = 'benign' | 'material' | 'stress' | 'noop';
export type VisualCaseSplit = 'training' | 'validation' | 'diagnostic';
export interface VisualTransformCase {
  readonly id: string;
  readonly label: VisualCaseLabel;
  readonly split: VisualCaseSplit;
  readonly kind: string;
  readonly value?: number;
  readonly seed?: number;
  readonly composed?: readonly string[];
  /** Editor rescaling must retain at least a 32-pixel comparison span at quarter size. */
  readonly minimumRegionDimension?: number;
}

const cases = (split: VisualCaseSplit, label: VisualCaseLabel,
  definitions: readonly Omit<VisualTransformCase, 'split' | 'label'>[]) =>
  definitions.map(definition => ({ ...definition, split, label }));

export const MACRO_NUISANCE_V1_CASES: readonly VisualTransformCase[] = [
  ...cases('training', 'benign', [
    { id: 'identity', kind: 'identity' },
    ...['x', 'y'].flatMap(axis => [-1, -0.5, 0.5, 1].map(value => ({ id: `translate-${axis}-${value}`, kind: `translate-${axis}`, value }))),
    ...[0.75, 1.25, 1.5].map(value => ({ id: `scale-${value}`, kind: 'scale', value })),
    { id: 'resample-0.75', kind: 'resample', value: 0.75 },
    ...[0.25, 0.5].flatMap(value => [
      { id: `editor-preview-${value}`, kind: 'resample', value, minimumRegionDimension: 128 },
      { id: `editor-preview-${value}-noise-3`, kind: 'composed', composed: [`editor-preview-${value}`, 'noise-3-12345'], minimumRegionDimension: 128 },
    ]),
    ...[0.25, 0.5].map(value => ({ id: `editor-preview-filtered-${value}`, kind: 'editor-preview-filtered', value, minimumRegionDimension: 128 })),
    ...[1, 2, 3, 4].map(seed => ({ id: `editor-preview-filtered-0.25-phase-${seed}`, kind: 'editor-preview-filtered', value: 0.25, seed, minimumRegionDimension: 128 })),
    { id: 'chroma-420', kind: 'chroma-420' },
    { id: 'matrix-bt601-decoded-bt709', kind: 'matrix-bt601-decoded-bt709' },
    { id: 'matrix-bt601-decoded-bt709-wide', kind: 'matrix-bt601-decoded-bt709-wide' },
    { id: 'chroma-420-unsharp-0.25', kind: 'composed', composed: ['chroma-420', 'unsharp-0.25'] },
    { id: 'resample-0.75-noise-3', kind: 'composed', composed: ['resample-0.75', 'noise-3-12345'] },
    { id: 'gaussian-0.5', kind: 'gaussian', value: 0.5 },
    { id: 'unsharp-0.25', kind: 'unsharp', value: 0.25 },
    ...[-2, 2].map(value => ({ id: `brightness-${value}`, kind: 'brightness', value })),
    ...[1, 2, 3, 4, 5, 6].map(seed => ({ id: `channel-offset-2-${seed}`, kind: 'channel-offset', value: 2, seed })),
    ...[0.98, 1.02].map(value => ({ id: `contrast-${value}`, kind: 'contrast', value })),
    ...[0.98, 1.02].map(value => ({ id: `gamma-${value}`, kind: 'gamma', value })),
    ...[1, 3].flatMap(value => [12345, 12346].map(seed => ({ id: `noise-${value}-${seed}`, kind: 'noise', value, seed }))),
  ]),
  ...cases('validation', 'benign', [
    ...['x', 'y'].flatMap(axis => [-0.75, -0.25, 0.25, 0.75].map(value => ({ id: `translate-${axis}-${value}`, kind: `translate-${axis}`, value }))),
    ...[0.9, 1.1].map(value => ({ id: `scale-${value}`, kind: 'scale', value })),
    { id: 'resample-0.9', kind: 'resample', value: 0.9 },
    ...[0.3125, 0.375, 0.625].map(value => ({ id: `editor-preview-${value}`, kind: 'resample', value, minimumRegionDimension: 128 })),
    { id: 'editor-preview-0.375-noise-2', kind: 'composed', composed: ['editor-preview-0.375', 'noise-2-23456'], minimumRegionDimension: 128 },
    ...[0.3125, 0.625].map(value => ({ id: `editor-preview-filtered-${value}`, kind: 'editor-preview-filtered', value, minimumRegionDimension: 128 })),
    ...[5, 6, 7, 8].map(seed => ({ id: `editor-preview-filtered-0.375-phase-${seed}`, kind: 'editor-preview-filtered', value: 0.375, seed, minimumRegionDimension: 128 })),
    { id: 'gaussian-0.25', kind: 'gaussian', value: 0.25 },
    ...[-1, 1].map(value => ({ id: `brightness-${value}`, kind: 'brightness', value })),
    ...[0.99, 1.01].map(value => ({ id: `contrast-${value}`, kind: 'contrast', value })),
    ...[0.99, 1.01].map(value => ({ id: `gamma-${value}`, kind: 'gamma', value })),
    ...[23456, 23457].map(seed => ({ id: `noise-2-${seed}`, kind: 'noise', value: 2, seed })),
    { id: 'resample-0.9-noise-2', kind: 'composed', composed: ['resample-0.9', 'noise-2-23456'] },
    { id: 'gamma-1.01-noise-2', kind: 'composed', composed: ['gamma-1.01', 'noise-2-23457'] },
  ]),
  ...[
    { id: 'rotate-90', kind: 'rotate' }, { id: 'mirror', kind: 'mirror' },
    { id: 'occlusion-center-10', kind: 'occlusion', value: 0.1 },
    { id: 'quadrant-reorder', kind: 'quadrants' },
    { id: 'horizontal-stripes', kind: 'stripes' }, { id: 'vertical-stripes', kind: 'stripes' },
    { id: 'contrast-patch-center', kind: 'patch', value: 0.05 },
    { id: 'contrast-patch-offset', kind: 'patch', value: 0.05 },
  ].flatMap(definition => [0, 1].map(seed => ({ ...definition, seed,
    id: `${definition.id}-${seed}`, label: 'material' as const,
    split: seed === 0 ? 'training' as const : 'validation' as const }))),
  { id: 'swap-red-blue-0', kind: 'channel-swap', seed: 0, label: 'material', split: 'training' },
  { id: 'swap-red-blue-half-right', kind: 'channel-swap', seed: 1, label: 'material', split: 'training' },
  { id: 'swap-red-blue-1', kind: 'channel-swap', seed: 2, label: 'material', split: 'validation' },
  ...Array.from({ length: 8 }, (_, seed) => ({ id: `background-occlusion-${seed}`, kind: 'occlusion', seed,
    value: seed < 4 ? 0.25 : 0.2, label: 'material' as const,
    split: seed < 4 ? 'training' as const : 'validation' as const })),
  { id: 'flat-black', kind: 'flat', value: 0, label: 'material', split: 'training' },
  { id: 'flat-white', kind: 'flat', value: 255, label: 'material', split: 'validation' },
  { id: 'mean-flat', kind: 'mean-flat', label: 'material', split: 'training' },
  { id: 'mean-occlusion-center-10', kind: 'mean-occlusion', value: 0.1, seed: 0, label: 'material', split: 'training' },
  { id: 'mean-occlusion-offset-25', kind: 'mean-occlusion', value: 0.25, seed: 1, label: 'material', split: 'validation' },
];

export class TransformPreset {
  public static qualifiedLabel(transform: VisualTransformCase, region: { width: number; height: number }): VisualCaseLabel {
    return Math.min(region.width, region.height) < (transform.minimumRegionDimension ?? 0) ? 'stress' : transform.label;
  }
  public static labelMateriality(reference: RgbSource, candidate: RgbSource): 'material' | 'stress' | 'noop' {
    return new VisualMaterialityPolicy().label(reference, candidate);
  }
}
